import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {defaultTheme} from '@stencil/build-theme';
import {CenteredSpaceWrap} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';

import HappyUncheckIcon from '@stencil/icons/src/HappyUncheckIcon/HappyUncheckIcon';
import HappyCheckIcon from '@stencil/icons/src/HappyCheckIcon/HappyCheckIcon';
import HappyDropDownIcon from '@stencil/icons/src/HappyDropDownIcon/HappyDropDownIcon';

import useToggle from '../../../../../shared/hooks/useToggle';
import useArrayToggler from '../../../../../shared/hooks/useArrayToggler';
import {ArrayTogglerBehaviorTypes} from '../../../../../shared/hooks/useArrayToggler';
import {isDefined} from './../../../../../types/type-guards';

interface ICheckBoxList<T extends unknown, K extends keyof T> {
  name: string;
  type: keyof typeof ArrayTogglerBehaviorTypes;
  data: T[];
  lookup: (item: T) => T[K] | T;
  renderItem: (item: T) => JSX.Element;
  actionHandler: (data: T[]) => void;
  resetHandlerProp?: () => void;
  defaultState?: T[];
  isSingleFilter?: boolean;
  maxLength?: number;
  contentsWrapperStyles?: {
    [K: string]: string; // 🐨 make it take only css properties
  };
}

function CheckBoxList<T extends unknown, K extends keyof T>(props: ICheckBoxList<T, K>) {
  const {
    name,
    type,
    data,
    lookup,
    renderItem,
    actionHandler,
    resetHandlerProp,
    defaultState,
    isSingleFilter = false,
    maxLength,
    contentsWrapperStyles = {},
  } = props;
  const [showMore, setShowMore] = useState(false);
  const [toggleValue, toggleHandler] = useToggle();
  const [isChecked, onClickHandler, resetHandler] = useArrayToggler(
    data,
    lookup,
    type,
    actionHandler,
    defaultState,
  );

  const hasAnyItemSelected = data.find(item => isChecked(item));

  return (
    <>
      <CheckboxListWrapperDiv isSingleFilter={isSingleFilter}>
        {(!isSingleFilter && (
          <CenteredSpaceWrap>
            <CenteredSpaceWrap
              onClick={() => {
                toggleHandler(!toggleValue);
              }}
            >
              {toggleValue ? (
                <HappyDropDownIcon
                  margin="0 1rem 0 0"
                  height="1rem"
                  width="1rem"
                  fill={defaultTheme.colors.downriver.base}
                />
              ) : (
                <ArrowDropDownCollapsedIcon
                  height="1rem"
                  width="1rem"
                  fill={defaultTheme.colors.downriver.base}
                />
              )}
              <StyledH5>{name}</StyledH5>
            </CenteredSpaceWrap>
            <div
              onClick={() => {
                resetHandler();
                if (isDefined(resetHandlerProp)) resetHandlerProp();
              }}
            >
              <ResetTextWrapper isactive={hasAnyItemSelected ? 'true' : 'false'}>
                RESET
              </ResetTextWrapper>
            </div>
          </CenteredSpaceWrap>
        )) ||
          null}

        {toggleValue &&
          data.map((item, index) => {
            if (!item) return;
            if (!showMore && maxLength && index > maxLength - 1) return;
            return (
              <div key={name + String(index)}>
                <TextCenteredDiv onClick={() => onClickHandler(item)} {...contentsWrapperStyles}>
                  {(isChecked(item) && (
                    <HappyCheckIcon
                      height="1.6rem"
                      width="1.6rem"
                      margin="0 1rem 0 0"
                      fill={defaultTheme.colors.goBrandBlue.base}
                    />
                  )) || <HappyUncheckIcon height="1.6rem" width="1.6rem" margin="0 1rem 0 0" />}
                  <CheckBoxListText>{renderItem(item)}</CheckBoxListText>
                </TextCenteredDiv>
              </div>
            );
          })}

        {(maxLength && (
          <div>
            <ShowAllText onClick={() => setShowMore(!showMore)}>
              {(!showMore && '+') || '-'} Show {(!showMore && 'All') || 'Less'}
            </ShowAllText>
          </div>
        )) ||
          null}
      </CheckboxListWrapperDiv>
    </>
  );
}

export default React.memo(CheckBoxList);

// need to decouple this singleFilter UI requirement from CheckBoxList
// currently CheckBoxList component is very much binded by its UI
const CheckboxListWrapperDiv = styled.div(
  props => `
  padding: ${props.isSingleFilter ? props.theme.spacing.small : props.theme.spacing.xlarge} ${
    props.theme.spacing.large
  } ${props.isSingleFilter ? props.theme.spacing.xlarge : props.theme.spacing.xlarge} ${
    props.theme.spacing.large
  };
  border-bottom: ${props.theme.misc.border.base} solid ${props.theme.colors.ash.medium};
`,
);

const TextCenteredDiv = styled.div`
  display: flex;
  cursor: pointer;
  width: ${props => (props.width ? props.width : 'auto')};
  padding-top: ${props => `${props.theme.spacing.large}`};
`;

const ShowAllText = styled.div(
  props => `
  cursor: pointer;
  color: ${props.theme.colors.goBrandBlue.base};
  padding-top: ${props.theme.spacing.large};
`,
);

const ResetTextWrapper = styled.span`
  font-size: ${props => props.theme.font.custom(12)};
  cursor: pointer;
  color: ${props =>
    props.isactive === 'true'
      ? props.theme.colors.goBrandBlue.base
      : props.theme.colors.downriver.medium};
`;

const StyledH5 = styled.h5(
  props => `
  font-size: ${props.theme.font.custom(14)};
  color:${props.theme.colors.downriver.base};
  font-weight:bold;

`,
);

const ArrowDropDownCollapsedIcon = styled(HappyDropDownIcon)`
  transform: rotate(-90deg);
  margin-right: 1rem;
`;

const CheckBoxListText = styled.span(
  props => `
  width:17.4rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`,
);
